import React from 'react';
import { Clock, Sparkles, Trash2, ChevronRight, ChevronLeft, ImageOff } from 'lucide-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiGetJson, apiDeleteJson } from '../../../../utils/api';

/**
 * Creative Studio history sidebar.
 *
 * Reads from /api/v1/creative_generations (thumbnails only).
 * On "Continue from here", fetches the full row (with thought_signature + full
 * image bytes) via /api/v1/creative_generations/:id and passes the payload to
 * the parent via `onContinueFrom`.
 *
 * Collapsible: when collapsed, shows a narrow vertical rail with a count badge
 * and a chevron to expand. Parent owns the open/closed state.
 */
const HistorySidebar = ({ onContinueFrom, currentParentId, collapsed = false, onToggle }) => {
  const queryClient = useQueryClient();

  const { data, isLoading, error } = useQuery({
    queryKey: ['creative-generations', 'index'],
    queryFn: () => apiGetJson('/creative_generations?per_page=30'),
    staleTime: 30_000,
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => apiDeleteJson(`/creative_generations/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['creative-generations', 'index'] });
    },
  });

  const handleContinue = async (id) => {
    try {
      const full = await apiGetJson(`/creative_generations/${id}`);
      if (full?.generation) onContinueFrom(full.generation);
    } catch (e) {
      console.error('Failed to load full generation:', e);
    }
  };

  const handleDelete = (id, e) => {
    e.stopPropagation();
    if (window.confirm('Delete this image from history? This cannot be undone.')) {
      deleteMutation.mutate(id);
    }
  };

  const generations = data?.generations || [];
  const cap = data?.retention_cap || 50;

  // Collapsed: narrow vertical rail. Click anywhere to expand.
  if (collapsed) {
    return (
      <aside
        className="w-10 flex-shrink-0 border-l border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 flex flex-col items-center py-3 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800/60 transition-colors"
        onClick={onToggle}
        title="Show creative history"
      >
        <ChevronLeft className="w-4 h-4 text-gray-400 mb-2" />
        <Clock className="w-4 h-4 text-blue-500" />
        <span className="mt-1 text-[10px] font-medium text-gray-600 dark:text-gray-300">
          {generations.length}
        </span>
        <span
          className="mt-2 text-[10px] uppercase tracking-wider text-gray-400 dark:text-gray-500"
          style={{ writingMode: 'vertical-rl', transform: 'rotate(180deg)' }}
        >
          History
        </span>
      </aside>
    );
  }

  return (
    <aside className="w-full lg:w-64 xl:w-72 flex-shrink-0 border-l border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 flex flex-col">
      <header className="px-3 py-2 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
        <div className="flex items-center gap-2 min-w-0">
          <Clock className="w-4 h-4 text-blue-600 dark:text-blue-400 flex-shrink-0" />
          <h3 className="text-sm font-semibold font-rasa text-gray-900 dark:text-white truncate">
            Creative history
          </h3>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-[10px] uppercase tracking-wide text-gray-500 dark:text-gray-400">
            {generations.length}/{cap}
          </span>
          {onToggle && (
            <button
              onClick={onToggle}
              className="p-0.5 rounded text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
              title="Collapse history"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          )}
        </div>
      </header>

      <div className="flex-1 overflow-y-auto">
        {isLoading && (
          <div className="p-6 text-center text-sm text-gray-400">Loading history…</div>
        )}

        {error && (
          <div className="p-4 m-3 rounded-md bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800/40 text-xs text-red-700 dark:text-red-300">
            Couldn't load history. {error.message}
          </div>
        )}

        {!isLoading && !error && generations.length === 0 && (
          <div className="p-6 text-center">
            <Sparkles className="w-8 h-8 mx-auto text-gray-300 dark:text-gray-600" />
            <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
              No images yet
            </p>
            <p className="mt-1 text-xs text-gray-400 dark:text-gray-500">
              Pick a variant and your kept images will appear here.
            </p>
          </div>
        )}

        <ul className="divide-y divide-gray-100 dark:divide-gray-800">
          {generations.map((gen) => {
            const isCurrent = gen.id === currentParentId;
            const thumbSrc = gen.thumbnail_base64
              ? `data:${gen.mime_type || 'image/jpeg'};base64,${gen.thumbnail_base64}`
              : null;

            return (
              <li
                key={gen.id}
                className={`relative group cursor-pointer transition-colors ${
                  isCurrent
                    ? 'bg-blue-50 dark:bg-blue-900/20'
                    : 'hover:bg-gray-50 dark:hover:bg-gray-800/60'
                }`}
                onClick={() => handleContinue(gen.id)}
              >
                <div className="flex items-start gap-3 p-3">
                  <div className="w-14 h-14 flex-shrink-0 rounded-md overflow-hidden bg-gray-100 dark:bg-gray-800 border border-gray-200 dark:border-gray-700">
                    {thumbSrc ? (
                      <img src={thumbSrc} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-gray-300 dark:text-gray-600">
                        <ImageOff className="w-5 h-5" />
                      </div>
                    )}
                  </div>

                  <div className="min-w-0 flex-1">
                    <p className="text-xs text-gray-900 dark:text-white font-medium line-clamp-2 leading-snug">
                      {gen.prompt}
                    </p>
                    <div className="flex items-center gap-1.5 mt-1 text-[10px] text-gray-500 dark:text-gray-400">
                      {gen.aspect_ratio && <span>{gen.aspect_ratio}</span>}
                      {gen.resolution && <span>·</span>}
                      {gen.resolution && <span>{gen.resolution}</span>}
                      <span>·</span>
                      <time dateTime={gen.created_at}>{relativeTime(gen.created_at)}</time>
                    </div>
                  </div>

                  <button
                    type="button"
                    onClick={(e) => handleDelete(gen.id, e)}
                    disabled={deleteMutation.isPending}
                    className="opacity-0 group-hover:opacity-100 transition-opacity p-1 rounded text-gray-400 hover:text-red-600 dark:hover:text-red-400 flex-shrink-0"
                    title="Delete from history"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>

                <div className="absolute inset-y-0 right-0 flex items-center pr-2 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
                  <ChevronRight className="w-4 h-4 text-blue-500" />
                </div>

                {isCurrent && (
                  <span className="absolute left-0 top-0 bottom-0 w-1 bg-blue-500" />
                )}
              </li>
            );
          })}
        </ul>
      </div>
    </aside>
  );
};

function relativeTime(iso) {
  if (!iso) return '';
  const now = Date.now();
  const t = new Date(iso).getTime();
  const sec = Math.max(0, Math.floor((now - t) / 1000));
  if (sec < 60) return 'just now';
  if (sec < 3600) return `${Math.floor(sec / 60)}m ago`;
  if (sec < 86400) return `${Math.floor(sec / 3600)}h ago`;
  if (sec < 604800) return `${Math.floor(sec / 86400)}d ago`;
  return new Date(iso).toLocaleDateString();
}

export default HistorySidebar;
